/**
 * Apple Pay free-shipping checkout button (iPhone only).
 * Clears cart, adds the current variant + bonus variant (qty 1), then opens checkout.
 */
class ApplePayFreeShippingButton extends HTMLElement {
  /** @type {HTMLButtonElement | null} */
  #button = null;

  connectedCallback() {
    if (!this.#shouldShow()) {
      this.hidden = true;
      return;
    }

    this.hidden = false;
    this.classList.add('apple-pay-free-shipping--visible');

    this.#button = this.querySelector('button');
    this.#button?.addEventListener('click', this.#onClick);
    document.addEventListener('variant:update', this.#onVariantUpdate);
    document.addEventListener('variant:selected', this.#onVariantSelected);
    this.#syncButtonState();
  }

  disconnectedCallback() {
    this.#button?.removeEventListener('click', this.#onClick);
    document.removeEventListener('variant:update', this.#onVariantUpdate);
    document.removeEventListener('variant:selected', this.#onVariantSelected);
  }

  #shouldShow() {
    if (this.dataset.designMode === 'true') return true;
    return /iPhone/i.test(navigator.userAgent) && !/iPad/i.test(navigator.userAgent);
  }

  /** @param {Event} event */
  #onVariantSelected = (event) => {
    if (!(event instanceof CustomEvent)) return;
    this.#applyVariant(event.detail?.resource);
  };

  /** @param {Event} event */
  #onVariantUpdate = (event) => {
    if (!(event instanceof CustomEvent)) return;

    const productId = this.dataset.productId;
    if (!productId || String(event.detail?.data?.productId) !== String(productId)) return;

    this.#applyVariant(event.detail?.resource);
  };

  /** @param {{ id?: string | number, available?: boolean } | null | undefined} variant */
  #applyVariant(variant) {
    if (variant?.id) {
      this.dataset.variantId = String(variant.id);
    }
    this.#syncButtonState(variant);
  }

  /** @param {{ id?: string | number, available?: boolean } | null | undefined} [variant] */
  #syncButtonState(variant) {
    if (!this.#button) return;

    const bonusVariantId = this.dataset.bonusVariantId;
    const variantId = variant?.id ? String(variant.id) : this.#getSelectedVariantId();
    const available = variant ? Boolean(variant.available) : Boolean(variantId);

    this.#button.disabled =
      !available || !variantId || !bonusVariantId || bonusVariantId === 'undefined';
  }

  #onClick = async () => {
    const variantId = this.#getSelectedVariantId();
    const bonusVariantId = await this.#getBonusVariantId();
    const discountCode = this.dataset.discountCode?.trim();

    if (!variantId || !bonusVariantId) return;

    const quantity = this.#getQuantity();
    const root = window.Shopify?.routes?.root || '/';

    this.#button?.setAttribute('aria-busy', 'true');
    if (this.#button) this.#button.disabled = true;

    try {
      await fetch(`${root}cart/clear.js`, {
        method: 'POST',
        headers: { Accept: 'application/json' },
      });

      const addResponse = await fetch(`${root}cart/add.js`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({
          items: [
            { id: Number(variantId), quantity },
            { id: Number(bonusVariantId), quantity: 1 },
          ],
        }),
      });

      if (!addResponse.ok) {
        this.#syncButtonState();
        return;
      }

      const checkoutUrl = `${root}checkout`;

      if (discountCode) {
        window.location.href = `${root}discount/${encodeURIComponent(discountCode)}?redirect=${encodeURIComponent(checkoutUrl)}`;
        return;
      }

      window.location.href = checkoutUrl;
    } catch {
      this.#syncButtonState();
    } finally {
      this.#button?.removeAttribute('aria-busy');
    }
  };

  /** @returns {string | null} */
  #getSelectedVariantId() {
    const productId = this.dataset.productId;
    const section = this.closest('[id^="shopify-section-"]');

    const formInput = section?.querySelector(
      `product-form-component[data-product-id="${productId}"] input[name="id"]`
    );

    if (formInput instanceof HTMLInputElement && formInput.value) {
      return formInput.value;
    }

    const pickerInput = section?.querySelector(
      `variant-picker[data-product-id="${productId}"] input[data-variant-id]:checked`
    );

    if (pickerInput instanceof HTMLInputElement && pickerInput.dataset.variantId) {
      return pickerInput.dataset.variantId;
    }

    const urlVariant = new URLSearchParams(window.location.search).get('variant');
    if (urlVariant) return urlVariant;

    const liquidVariantId = this.dataset.variantId;
    if (liquidVariantId && liquidVariantId !== 'undefined') return liquidVariantId;

    return null;
  }

  /** @returns {number} */
  #getQuantity() {
    const productId = this.dataset.productId;
    const section = this.closest('[id^="shopify-section-"]');
    const quantityInput = section?.querySelector(
      `product-form-component[data-product-id="${productId}"] quantity-selector-component input[ref="quantityInput"]`
    );

    if (quantityInput instanceof HTMLInputElement) {
      const value = parseInt(quantityInput.value, 10);
      if (!Number.isNaN(value) && value > 0) return value;
    }

    const minQuantity = parseInt(this.dataset.minQuantity || '1', 10);
    return Number.isNaN(minQuantity) || minQuantity < 1 ? 1 : minQuantity;
  }

  /** @returns {Promise<string | null>} */
  async #getBonusVariantId() {
    const existingId = this.dataset.bonusVariantId;
    if (existingId && existingId !== 'undefined') return existingId;

    const handle = this.dataset.bonusProductHandle;
    if (!handle) return null;

    const root = window.Shopify?.routes?.root || '/';

    try {
      const response = await fetch(`${root}products/${handle}.js`);
      if (!response.ok) return null;

      const product = await response.json();
      const variantId = product?.variants?.[0]?.id;
      if (variantId) {
        this.dataset.bonusVariantId = String(variantId);
        return String(variantId);
      }
    } catch {
      return null;
    }

    return null;
  }
}

if (!customElements.get('apple-pay-free-shipping-button')) {
  customElements.define('apple-pay-free-shipping-button', ApplePayFreeShippingButton);
}
